import { DecimalMath, formatNumber } from "@/lib/decimal-utils";

// Base unit: Joules (J)
const CONVERSION_FACTORS_TO_J = {
  J: 1,
  kWh: 3600000, // 1 kWh = 1000 Wh = 3,600,000 J
  MWh: 3600000000, // 1 MWh = 1000 kWh = 3,600,000,000 J
} as const;

const CONVERSION_FACTOR_T_PER_MWH_TO_KG_PER_J = 0.000000277777777777778; // = 2.777777777777778e-7 precision: 10 decimal places
const CONVERSION_FACTOR_KG_PER_J_TO_T_PER_MWH = 3600000; // = 3.6e6

const UNIT_NAMES = {
  J: "Joules",
  kWh: "Kilowatt Hours",
  MWh: "Megawatt Hours",
} as const;

export const CONSUMPTION_UNIT = "MWh";
export const EMISSION_OUTPUT_UNIT = "tCO₂äq";
export const EMISSION_OUTPUT_PER_YEAR_UNIT = "tCO₂äq/Jahr";

export const EMISSION_FACTOR_UNIT = "tCO₂äq/MWh";

export type EnergyUnit = keyof typeof CONVERSION_FACTORS_TO_J;

/**
 * True when `unit` is a known energy unit we can convert to/from Joules.
 * Custom-unit sectors carry an arbitrary unit string (e.g. "Tonnen") that is
 * not energy — those values are stored and displayed raw (no conversion).
 */
export function isEnergyUnit(unit: string): unit is EnergyUnit {
  return Object.prototype.hasOwnProperty.call(CONVERSION_FACTORS_TO_J, unit);
}

export const MILEAGE_UNIT_STREET_VEHICLES = "Fz-km";
export const MILEAGE_UNIT_TRAIN_KILOMETERS = "Zug-km";
export const SPECIFIC_CONSUMPTION_FACTOR_UNIT_VEHICLE_KM = "kWh/Fz-km";
export const SPECIFIC_CONSUMPTION_FACTOR_UNIT_PASSENGER_KM = "kWh/Personen-km";

/**
 * Convert energy value from one unit to another
 * @param value - The value to convert
 * @param fromUnit - The source unit
 * @param toUnit - The target unit
 * @returns The converted value
 */
export function convertEnergy(value: number, fromUnit: EnergyUnit, toUnit: EnergyUnit): number {
  // Convert to base unit (Joules) first
  const valueInJoules = DecimalMath.multiply(value, CONVERSION_FACTORS_TO_J[fromUnit]);
  // Then convert to target unit
  return DecimalMath.round(DecimalMath.divide(valueInJoules, CONVERSION_FACTORS_TO_J[toUnit]), 2);
}

/**
 * Convert energy value to Joules
 * @param value - The value to convert
 * @param fromUnit - The source unit
 * @returns The value in Joules
 */
export function toJoules(value: number, fromUnit: string): number {
  // Custom (non-energy) units are stored raw — there is nothing to convert.
  if (!isEnergyUnit(fromUnit)) {
    return DecimalMath.round(value, 2);
  }
  return DecimalMath.round(DecimalMath.multiply(value, CONVERSION_FACTORS_TO_J[fromUnit]), 2);
}

/**
 * Convert energy value from Joules to another unit
 * @param joules - The value in Joules
 * @param toUnit - The target unit
 * @returns The converted value
 */
export function fromJoules(joules: number, toUnit: string): number {
  // Custom (non-energy) units were stored raw — return the value unchanged.
  if (!isEnergyUnit(toUnit)) {
    return DecimalMath.round(joules, 2);
  }
  return DecimalMath.round(DecimalMath.divide(joules, CONVERSION_FACTORS_TO_J[toUnit]), 2);
}

/**
 * Format energy value with appropriate unit
 * @param value - The value to format
 * @param unit - The unit of the value
 * @param useFullName - Whether to use the full unit name (default: false)
 * @param decimals - Number of decimal places (default: 2, use 0 or negative for integers)
 * @returns Formatted string with value and unit
 */
export function formatEnergy(
  value: number,
  unit: string,
  useFullName: boolean = false,
  decimals: number = 2,
): string {
  const unitName = useFullName && isEnergyUnit(unit) ? UNIT_NAMES[unit] : unit;
  const formattedValue = decimals <= 0 ? Math.round(value).toLocaleString() : formatNumber(value, decimals);
  return `${formattedValue} ${unitName}`;
}

export const convertKgPerJToTPerMWh = (value: number): number => {
  const result = DecimalMath.multiply(value, CONVERSION_FACTOR_KG_PER_J_TO_T_PER_MWH);
  return DecimalMath.round(result, 3);
};

export const convertTPerMWhToKgPerJ = (value: number): number => {
  const result = DecimalMath.multiply(value, CONVERSION_FACTOR_T_PER_MWH_TO_KG_PER_J);
  return result;
};

const KG_PER_T = 1000;

export const convertFactorToStorage = (value: number, unit: string): number =>
  isEnergyUnit(unit) ? convertTPerMWhToKgPerJ(value) : DecimalMath.multiply(value, KG_PER_T);

export const convertFactorFromStorage = (value: number, unit: string): number =>
  isEnergyUnit(unit)
    ? convertKgPerJToTPerMWh(value)
    : DecimalMath.round(DecimalMath.divide(value, KG_PER_T), 6);

export const getEmissionFactorUnit = (unit: string): string =>
  isEnergyUnit(unit) ? EMISSION_FACTOR_UNIT : `tCO₂äq/${unit}`;
